import type { HotelFilterPillData } from '@apps/main/new-desktop/modules/hotelFilters';
import type { HotelAmenityData } from '@apps/main/new-desktop/core/models';
import type { GuestRatingSummary } from '@apps/main/new-desktop/modules/customersRating';

import type {
  AccommodationType,
  FullStars,
  IngredientId,
  Money,
} from '@libs/trip-planner-interfaces';
import { exhaustiveGuard } from '@libs/utils';

import type { HotelStayTag } from '../../../../modules/stayTag';

import type { ItinerarySegmentType } from './itinerarySegmentType';
import type {
  ProductCardTopBarType,
  ProductInfo,
  ProductRating,
  UnavailabilityReason,
} from './shared';

export const enum ItineraryAccommodationSegmentStatus {
  NoAvailable = 'noAvailable',
  Available = 'availableEnabled',
  AlternativeRoom = 'alternativeRoom',
}

export interface AccommodationProductCardContent {
  header: string;
  subheader: string;
  rating: ProductRating;
  cardDetails: ProductInfo[];
  changeButtonText: string;
  amenities: HotelAmenityData[][];
  mealName?: string;
  cancellationPolicies?: HotelStayTag;
  guestRating?: GuestRatingSummary;
}

export interface AccommodationSegmentContext {
  ingredientId: IngredientId;
  ingredientIndex: number;
  hotelCode: string;
  accommodationType: AccommodationType;
}

export interface AccommodationProductCardData {
  imgSrcs: string[];
  productCardContent: AccommodationProductCardContent;
  date: string;
}

export interface AvailableItineraryAccommodationSegment {
  segmentType: ItinerarySegmentType.Accommodation;
  statusType: ItineraryAccommodationSegmentStatus.Available;
  productCardData: AccommodationProductCardData;
  addToBasketToken: string;
  context: AccommodationSegmentContext;
  productCardTopBarType: ProductCardTopBarType;
  filters: HotelFilterPillData[];
  alternativeHotels: AlternativeHotel[];
  accommodationAmountInDestination?: number;
}

export interface AlternativeHotel {
  imgUrls: string[];
  hotelName: string;
  polygonName: string;
  hotelCode: string;
  deltaPrice: Money;
  accommodationType: AccommodationType;
  ingredientId: IngredientId;
  stars: FullStars;
  amenities: HotelAmenityData[][];
  isRefundable: boolean;
  roomsInfo: ProductInfo[];
  mealName?: string;
  guestRating?: GuestRatingSummary;
}

export interface UnavailableAccommodationSegmentContext {
  ingredientId: IngredientId;
  ingredientIndex: number;
}

export interface UnavailableItineraryAccommodationSegment {
  segmentType: ItinerarySegmentType.Accommodation;
  statusType: ItineraryAccommodationSegmentStatus.NoAvailable;
  productCardData: null;
  context: UnavailableAccommodationSegmentContext;
  reasons: UnavailabilityReason[];
  filters: HotelFilterPillData[];
}

export interface AlternativeSuggestionData {
  isRefundable: boolean;
  hotelCode: string;
  rooms: {
    roomCode: string;
    mealCode: string;
  }[];
}

export interface AlternativeRoomSuggestionAccommodationSegment {
  segmentType: ItinerarySegmentType.Accommodation;
  statusType: ItineraryAccommodationSegmentStatus.AlternativeRoom;
  productCardData: AccommodationProductCardData;
  context: AccommodationSegmentContext;
  alternativeSuggestionData: AlternativeSuggestionData;
  productCardTopBarType: ProductCardTopBarType;
  filters: HotelFilterPillData[];
}

export type ItineraryAccommodationSegment =
  | AvailableItineraryAccommodationSegment
  | UnavailableItineraryAccommodationSegment
  | AlternativeRoomSuggestionAccommodationSegment;

export function isAlternativeRoomSuggestionAccommodationSegment(
  segment: ItineraryAccommodationSegment
): segment is AlternativeRoomSuggestionAccommodationSegment {
  switch (segment.statusType) {
    case ItineraryAccommodationSegmentStatus.NoAvailable:
    case ItineraryAccommodationSegmentStatus.Available:
      return false;

    case ItineraryAccommodationSegmentStatus.AlternativeRoom:
      return true;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isAvailableAccommodationSegment(
  segment: ItineraryAccommodationSegment
): segment is AvailableItineraryAccommodationSegment {
  switch (segment.statusType) {
    case ItineraryAccommodationSegmentStatus.Available:
      return true;

    case ItineraryAccommodationSegmentStatus.AlternativeRoom:
    case ItineraryAccommodationSegmentStatus.NoAvailable:
      return false;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isUnavailableAccommodationSegment(
  segment: ItineraryAccommodationSegment
): segment is UnavailableItineraryAccommodationSegment {
  switch (segment.statusType) {
    case ItineraryAccommodationSegmentStatus.NoAvailable:
      return true;

    case ItineraryAccommodationSegmentStatus.AlternativeRoom:
    case ItineraryAccommodationSegmentStatus.Available:
      return false;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}
